import { DocumentCard, DocumentCardImage, IDropdownOption, ImageFit } from "@fluentui/react";
import { Utils } from "../../../common/views/Utils";
import { SortDirection } from "../../../home/models/PageTypes";
import { OrderStatus, OrderRejectReason, OrderTracking, OrderModel } from "../../models/OrderModel";

export class OrderUtils {
    public static getAmount(order: OrderModel): number {
        return order.price * order.qty * (100 + order.rate) / 100;
    }

    public static getTotal(order: OrderModel): number {
        const amount = this.getAmount(order);
        return amount + order.domesticShipping + order.chargeWeight * order.shippingRate;
    }

    public static getStatusText(status: OrderStatus): string {
        switch (status) {
            case OrderStatus.all: return "All";
            case OrderStatus.request: return "Request";
            case OrderStatus.quote: return "Quote";
            case OrderStatus.orderPending: return "Pending";
            case OrderStatus.orderApproved: return "Approved";
            case OrderStatus.completed: return "Completed";
            case OrderStatus.declined: return "Declined";
            case OrderStatus.canceled: return "Canceled";
        }

        return "";
    }

    public static getStatusOptions(): IDropdownOption[] {
        const list = [
            { key: OrderStatus.request, text: "Request" },
            { key: OrderStatus.quote, text: "Quote" },
            { key: OrderStatus.orderPending, text: "Order Pending" },
            { key: OrderStatus.orderApproved, text: "Order Approved" },
            { key: OrderStatus.completed, text: "Completed" },
            { key: OrderStatus.declined, text: "Declined" },
            { key: OrderStatus.canceled, text: "Canceled" }
        ];

        return list;
    }

    public static getReasonOptions() {
        const list = [
            { key: OrderRejectReason.none, text: "None" },
            { key: OrderRejectReason.changeMind, text: "Change Mind" },
            { key: OrderRejectReason.damaged, text: "Damaged" },
            { key: OrderRejectReason.outOfStock, text: "Out of Stock" },
            { key: OrderRejectReason.priceTooHigh, text: "Price Too High" },
            { key: OrderRejectReason.waitTooLong, text: "Wait Too Long" }
        ];

        return list;
    }

    public static getTrackingOptions() {
        const list = [
            { key: OrderTracking.none, text: "None" },
            { key: OrderTracking.placedOnline, text: "Placed" },
            { key: OrderTracking.inForeignStock, text: "At WH" },
            { key: OrderTracking.shippedToDestination, text: "Shipped" },
            { key: OrderTracking.arrivedDestination, text: "Arrived" },
            { key: OrderTracking.readyForDelivery, text: "Ready Delivery" },
            { key: OrderTracking.outForDelivery, text: "Out Delivery" },
            { key: OrderTracking.delivered, text: "Delivered" }
        ];

        return list;
    }

    public static getSortOptions() {
        const list = [
            { key: 0, name: "None", direction: SortDirection.descending, text: "" },
            { key: 1, name: "Date", direction: SortDirection.descending, text: "Date Desc" },
            { key: 2, name: "Status", direction: SortDirection.ascending, text: "Status" },
            { key: 3, name: "Reason", direction: SortDirection.ascending, text: "Reason" },
            { key: 4, name: "Tracking", direction: SortDirection.ascending, text: "Tracking Asc" },
            { key: 5, name: "Tracking", direction: SortDirection.descending, text: "Tracking Desc" },
            { key: 6, name: "customer", direction: SortDirection.descending, text: "Customer" },
            { key: 7, name: "orderId", direction: SortDirection.descending, text: "Order #" },
            { key: 8, name: "invoiceId", direction: SortDirection.descending, text: "Invoice #" },
            { key: 9, name: "price", direction: SortDirection.descending, text: "Price Asc" },
            { key: 10, name: "price", direction: SortDirection.descending, text: "Date Desc" },
            { key: 11, name: "productName", direction: SortDirection.descending, text: "Product Name" },
            { key: 12, name: "productId", direction: SortDirection.descending, text: "Product #" }
        ];

        return list;
    }

    public static isPendingStatus(status: OrderStatus) {
        return status === OrderStatus.request ||
            status === OrderStatus.quote ||
            status === OrderStatus.orderPending;
    }

    public static isOrderStatus(status: OrderStatus) {
        return status === OrderStatus.orderPending ||
            status === OrderStatus.orderApproved ||
            status === OrderStatus.completed;
    }

    public static image = (url: string, qty: number, size: number, total: number, includeTotal: boolean) => {
        return <DocumentCard className="picture-wrapper" style={{ minWidth: "100%" }}>
            <DocumentCardImage
                imageFit={ImageFit.centerCover}
                className="product-picture"
                height={size}
                imageSrc={url}
            />

            {qty !== 0 && <div className="right" style={{ position: "relative", marginTop: -20 }}>
                <span className="count">{qty}</span>
            </div>}

            {includeTotal && <div className="center pt-5">
                <span className="font-smaller">est. $ {total.toFixed(0)}</span>
            </div>}
        </DocumentCard>
    }

    public static ProductInfo = (order: OrderModel) => {
        const product = order.product;

        return <div className="left">
            {product && <div>
                {!Utils.isNullOrEmpty(product.name) && <div className="limit-lines one-line">
                    {product.name}
                </div>}

                <div className="gray font-smaller">
                    {!Utils.isNullOrEmpty(product.description) && <div className="pt-5 limit-lines two-lines">
                        {product.description}
                    </div>}

                    {product.weight > 0 && <div className="pt-5">
                        Weight: {Utils.formatNumber(product.weight)} lbs
                    </div>}

                    {!Utils.isNullOrEmpty(product.dimension) && <div className="pt-5">
                        {product.dimension}
                    </div>}
                </div>
            </div>}
        </div>
    }

    public static ShowTracking = (order: OrderModel): boolean => {
        return order.status === OrderStatus.orderApproved || order.status === OrderStatus.completed;
    }
}